import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { getSettings } from "../../api/settingsApi";
import { useQuery } from "@tanstack/react-query";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white hover:bg-primary/90 border border-gray-200 dark:border-[#30363d]",
        bits: "bg-bits-blue text-white hover:bg-bits-blue/90 border border-bits-blue",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-gray-200 bg-white text-foreground hover:bg-gray-50 hover:text-primary dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#161b22] dark:hover:text-[#f0f6fc]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-gray-200 dark:bg-[#21262d] dark:text-[#c9d1d9] dark:border-[#30363d] dark:hover:bg-[#30363d]",
        ghost:
          "hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100",
        link: "border border-gray-200 text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<"button">, "ref"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  const settingsQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getSettings,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 10 * 60 * 1000,
  });
  const primaryColor = settingsQuery?.data?.data?.appearance?.primaryColor;
  // console.log("variant", variant);
  return (
    <Comp
      data-slot="button"
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      style={
        primaryColor
          ? variant === "bits"
            ? {
                backgroundColor: primaryColor,
                borderColor: primaryColor,
              }
            : variant === "outline"
              ? {
                  borderColor: primaryColor,
                }
              : undefined
          : undefined
      }
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
